import * as S from './styles';

interface StoryPreviewProps {
	storyHtml: string;
	isValidHtml?: boolean;
	rawCode: {
		tokens: string;
		primitives: string;
		components: string;
	};
}

export const StoryPreview = ({ storyHtml, isValidHtml = true, rawCode }: StoryPreviewProps) => {
	// If story doesn't contain valid HTML, show error message instead of iframe
	if (isValidHtml === false) {
		return (
			<S.BrokenStoryContainer>
				<S.BrokenStoryIcon>⚠️</S.BrokenStoryIcon>
				<S.BrokenStoryTitle>Broken Story</S.BrokenStoryTitle>
				<S.BrokenStoryMessage>
					This story contains invalid HTML code and cannot be rendered. The story code may have been
					corrupted or contains plain text instead of HTML.
				</S.BrokenStoryMessage>
				<S.BrokenStoryCode>
					<pre>{storyHtml}</pre>
				</S.BrokenStoryCode>
			</S.BrokenStoryContainer>
		);
	}

	// Combine story HTML with design system CSS and JS into a complete HTML document
	const fullHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
	<meta charset="UTF-8">
	<meta name="viewport" content="width=device-width, initial-scale=1.0">
	<title>Story Preview</title>
	<style>
		${rawCode.tokens || ''}
	</style>
	<style>
		${rawCode.primitives || ''}
	</style>
	<style>
		body {
			margin: 0;
			padding: 20px;
			font-family: var(--ds-font-sans, system-ui, sans-serif);
			background: var(--ds-color-bg, #f8fbff);
			color: var(--ds-color-text, #0f172a);
		}
	</style>
</head>
	<body>
	${storyHtml}
	<script>
		${rawCode.components || ''}
	</script>
	<script>
		// Prevent hash links from navigating the iframe to parent URL
		// srcDoc iframes resolve hash links relative to parent document
		document.addEventListener('click', function(e) {
			const anchor = e.target.closest('a');
			if (anchor && anchor.getAttribute('href')?.startsWith('#')) {
				e.preventDefault();
			}
		}, true);
	</script>
</body>
</html>
	`.trim();

	return (
		<iframe
			srcDoc={fullHtml}
			style={{
				width: '100%',
				height: '100%',
				border: 'none',
				background: '#ffffff',
			}}
			title="Story Preview"
			sandbox="allow-scripts"
		/>
	);
};
